import { Eye, ShieldCheck, Trophy, Zap } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { CTABanner } from "../components/CTABanner";
import { BrandsMarquee } from "../components/BrandsMarquee";

const approach = [
  {
    num: "01",
    title: "Strategy",
    desc: "Every campaign starts with clear goals, audience research and competitor insights.",
  },
  {
    num: "02",
    title: "Execution",
    desc: "Creative, copy, targeting and tracking set up properly from day one.",
  },
  {
    num: "03",
    title: "Optimisation",
    desc: "Ongoing testing and data-backed improvements to lower costs and increase returns.",
  },
];

const values = [
  { icon: Eye, title: "Transparency" },
  { icon: ShieldCheck, title: "Accountability" },
  { icon: Zap, title: "Speed" },
  { icon: Trophy, title: "Results" },
];

export default function About() {
  return (
    <>
      <Seo
        title="About Chatbudds — Marketing Accountable to Your Numbers"
        description="Chatbudds is a performance marketing agency focused on one thing: profitable Meta Ads and Google Ads campaigns with honest strategy and transparent reporting."
      />

      <section className="relative overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 dot-grid" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16 md:pt-44 md:pb-24">
          <Reveal>
            <span className="inline-flex items-center rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-700" data-testid="about-eyebrow">
              About Us
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.06]">
              Marketing that's accountable to <span className="text-brand-600">your numbers</span>.
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24" data-testid="about-who-we-are">
        <div className="grid gap-8 lg:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-3xl border border-slate-200 bg-white p-8 sm:p-10">
              <h2 className="font-display text-2xl font-bold text-slate-900">Who we are</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">
                Chatbudds is a performance marketing agency focused on one thing: running profitable paid ad
                campaigns. We work with businesses across industries to generate leads, sales and measurable
                growth through Meta Ads and Google Ads.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="h-full rounded-3xl bg-brand-600 p-8 sm:p-10 text-white" data-testid="about-mission-card">
              <h2 className="font-display text-2xl font-bold">Our mission</h2>
              <p className="mt-4 text-base leading-relaxed text-brand-50">
                To help businesses grow faster by making every ad rupee work harder, with honest strategy, sharp
                execution and transparent reporting.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-slate-50/70" data-testid="about-approach">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <Reveal>
            <span className="inline-flex items-center rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-700">
              Our approach
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 max-w-2xl font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              A simple system, run with discipline.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3 md:gap-8">
            {approach.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.12}>
                <div
                  data-testid={`approach-card-${i + 1}`}
                  className="h-full rounded-3xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:border-brand-300 hover:shadow-lg hover:shadow-brand-900/5"
                >
                  <span className="font-display text-5xl font-bold text-brand-100">{a.num}</span>
                  <h3 className="mt-4 font-display text-xl font-bold text-slate-900">{a.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{a.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="about-values">
        <Reveal>
          <span className="inline-flex items-center rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-700">
            Our values
          </span>
        </Reveal>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {values.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.1}>
              <div
                data-testid={`value-card-${i + 1}`}
                className="flex items-center gap-4 rounded-3xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:border-brand-300 hover:shadow-lg hover:shadow-brand-900/5"
              >
                <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-brand-600 text-white">
                  <v.icon className="h-5 w-5" />
                </span>
                <h3 className="font-display text-lg font-bold text-slate-900">{v.title}</h3>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="pb-16 md:pb-24" data-testid="about-brands">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              Brands we have worked with
            </h2>
          </Reveal>
        </div>
        <BrandsMarquee />
        <p className="mt-6 text-center text-sm font-medium text-slate-500">…and many more.</p>
      </section>

      <CTABanner
        title="Want to be our next success story?"
        buttonLabel="Talk to Us"
        testid="about-final-cta-button"
      />
    </>
  );
}
